import { Link } from 'react-router-dom';
import { CustomerBadges } from './TripExtras';
import { ChevronRight } from 'lucide-react';
import { Card, Money, RequestStatusBadge, RideStatusBadge } from '@tcrd/ui';
import { fmtDateTime } from '../lib/format';
import type { RideDto, RideRequestDto } from '../lib/types';

function RouteLines({ from, to }: { from: string; to: string }) {
  return (
    <div className="mt-2 grid grid-cols-[12px_1fr] gap-x-3 gap-y-1 text-sm">
      <span className="mt-1.5 size-2.5 rounded-full bg-brand-black dark:bg-white" aria-hidden />
      <span className="truncate">{from}</span>
      <span className="mt-1.5 size-2.5 rounded-full bg-brand-red" aria-hidden />
      <span className="truncate">{to}</span>
    </div>
  );
}

export function RequestCard({ r, href }: { r: RideRequestDto; href: string }) {
  const pending = r.quotes.find((q) => q.status === 'PENDING');
  return (
    <Link to={href}>
      <Card className="p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold">{r.service.name}</span>
          <RequestStatusBadge status={r.status} />
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-0.5">
          {r.mode === 'NOW' ? 'Lo antes posible' : r.mode === 'RECURRING' ? 'Viaje recurrente' : fmtDateTime(r.scheduledAt)}
          {r.customer ? ` · ${r.customer.firstName} ${r.customer.lastName}` : ''}
        </p>
        {r.customer && <CustomerBadges c={r.customer} className="mt-1.5" />}
        <RouteLines from={r.origin.address} to={r.destination.address} />
        {r.offeredFare && !pending && (
          <p className="mt-3 text-sm font-semibold flex items-center justify-between">
            Ofrece: <Money value={r.offeredFare} className="text-green-700 dark:text-green-400" />
          </p>
        )}
        {pending && (
          <p className="mt-3 text-sm font-semibold flex items-center justify-between">
            Cotización: <Money value={pending.amount} className="text-brand-red" />
          </p>
        )}
      </Card>
    </Link>
  );
}

export function RideCard({ r, href }: { r: RideDto; href: string }) {
  return (
    <Link to={href}>
      <Card className="p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold">{fmtDateTime(r.scheduledAt)}</span>
          <RideStatusBadge status={r.status} />
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-0.5">
          {r.code} · {r.service.name}
          {r.leg === 'RETURN' ? ' · Regreso' : r.leg === 'OUTBOUND' ? ' · Ida' : ''}
          {r.customer ? ` · ${r.customer.firstName} ${r.customer.lastName}` : ''}
        </p>
        <RouteLines from={r.origin.address} to={r.destination.address} />
        <div className="mt-3 flex items-center justify-between text-sm">
          <Money value={r.agreedFare} className="font-semibold" />
          <ChevronRight className="size-5 text-[var(--text-muted)]" aria-hidden />
        </div>
      </Card>
    </Link>
  );
}
